import { QueryResult } from '@apollo/client';
import {
  DocumentQuery,
  DocumentQueryVariables,
  DocumentUpdateMutationVariables,
  useDocumentQuery,
  useDocumentUpdateMutation,
} from 'generated/graphql';
import { ReactEventHandler } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

interface FormData {
  name: string;
  nonExpiring: boolean;
  expiresOn: string;
}

const getFormData = ({ document }: DocumentQuery): FormData => ({
  name: document.name,
  nonExpiring: document.nonExpiring,
  expiresOn: document.expiresOn,
});

const getMutationData = (data: FormData): DocumentUpdateMutationVariables['data'] => ({
  nonExpiring: data.nonExpiring,
  expiresOn: data.nonExpiring ? null : data.expiresOn,
});

export const useDocumentSettingsForm = (): {
  document: QueryResult<DocumentQuery, DocumentQueryVariables>;
  form: UseFormReturn<FormData, any>;
  onSubmit: ReactEventHandler;
  loading: boolean;
  initialLoadDone: boolean;
} => {
  const form = useForm<FormData>();
  const { documentId: id } = useParams() as { documentId: string };
  const navigate = useNavigate();
  const goBack = () => navigate(-1);

  const document = useDocumentQuery({
    variables: { id },
    onCompleted: data => form.reset(getFormData(data)),
  });

  const [updateDocument, { loading }] = useDocumentUpdateMutation({ onCompleted: goBack });

  const onSubmit = form.handleSubmit(values =>
    updateDocument({ variables: { id, data: getMutationData(values) } })
  );

  return {
    document,
    form,
    onSubmit,
    loading: document.loading || loading,
    // workaround for chakra checkboxes not rerendering when form is reset
    // this will be used to only render the form after the after the query when the state has been reset
    initialLoadDone: !document.loading,
  };
};
